import { useCallback, useMemo } from "react";
import { Source, Layer } from "react-map-gl/mapbox";
import type { MapMouseEvent } from "react-map-gl/mapbox";
import type { PlaceFeature, PlaceGeoJSON } from "../../../lib/api";
import {
  ADVENTURE_MAP_PLACES_LAYER_ID,
  ADVENTURE_MAP_PLACES_SOURCE_ID,
  adventureMapPlacesLayout,
  placePaint,
} from "../../../lib/mapConstants";
import { useMapEvent } from "../hooks/useMapEvent";
import { useCursorOnHover } from "../hooks/useCursorOnHover";

interface AdventureMapPlacesLayerProps {
  /** GeoJSON FeatureCollection of adventure map places to display */
  places: PlaceGeoJSON;
  /** Place IDs to exclude from rendering (e.g., related places shown in another layer) */
  excludeIds?: string[];
  /** Called when a place is clicked */
  onClick?: (feature: PlaceFeature) => void;
}

/**
 * Renders adventure map places with highlighted icons (poi-{type}-highlighted).
 * Handles click events and cursor feedback internally.
 */
export function AdventureMapPlacesLayer({ places, excludeIds, onClick }: AdventureMapPlacesLayerProps) {
  const filteredPlaces = useMemo((): PlaceGeoJSON => {
    if (!excludeIds || excludeIds.length === 0) return places;
    const excludeSet = new Set(excludeIds);
    return {
      ...places,
      features: places.features.filter((f) => !excludeSet.has(f.id as string)),
    };
  }, [places, excludeIds]);

  // Handle click - find the full feature from our data
  const handleClick = useCallback(
    (e: MapMouseEvent) => {
      if (!onClick) return;
      const clickedFeature = e.features?.[0];
      if (!clickedFeature?.properties?.id) return;

      // Find the full feature from our places data (event features lose some properties)
      const placeId = clickedFeature.properties.id as string;
      const feature = places.features.find((f) => f.id === placeId);
      if (feature) {
        onClick(feature);
      }
    },
    [places.features, onClick],
  );

  useMapEvent("click", ADVENTURE_MAP_PLACES_LAYER_ID, handleClick);
  useCursorOnHover(ADVENTURE_MAP_PLACES_LAYER_ID);

  return (
    <Source
      id={ADVENTURE_MAP_PLACES_SOURCE_ID}
      type="geojson"
      data={filteredPlaces}
      attribution={places.attribution ?? ""}
    >
      <Layer id={ADVENTURE_MAP_PLACES_LAYER_ID} type="symbol" layout={adventureMapPlacesLayout} paint={placePaint} />
    </Source>
  );
}
